import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { FacultyLayout } from '../../components/FacultyLayout';
import { Card, CardContent } from '../../components/ui/card';
import { User } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FacultyProfile = () => {
    const { token, user } = useAuth();
    const [faculty, setFaculty] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchFacultyData = async () => {
            try {
                const headers = { Authorization: `Bearer ${token}` };
                const response = await axios.get(`${API_URL}/api/faculty`, { headers });
                // Find faculty by email
                const facultyData = response.data.find(f => f.email === user.email);
                setFaculty(facultyData);
            } catch (error) {
                console.error('Error fetching faculty data:', error);
            } finally {
                setLoading(false);
            }
        };

        if (user) {
            fetchFacultyData();
        }
    }, [token, user]);

    if (loading) {
        return (
            <FacultyLayout title="My Profile">
                <Card className="animate-pulse">
                    <CardContent className="h-64"></CardContent>
                </Card>
            </FacultyLayout>
        );
    }

    if (!faculty) {
        return (
            <FacultyLayout title="My Profile">
                <Card>
                    <CardContent className="py-16 text-center">
                        <User className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                        <h3 className="font-heading text-xl font-semibold mb-2">Profile Not Found</h3>
                        <p className="text-muted-foreground">
                            Your faculty profile hasn't been set up yet. Please contact the administrator.
                        </p>
                    </CardContent>
                </Card>
            </FacultyLayout>
        );
    }

    return (
        <FacultyLayout title="My Profile">
            <div className="max-w-3xl">
                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-start gap-6">
                            {faculty.photo_url ? (
                                <img
                                    src={faculty.photo_url.startsWith('http') ? faculty.photo_url : `${API_URL}${faculty.photo_url.startsWith('/') ? '' : '/'}${faculty.photo_url}`}
                                    alt={faculty.name}
                                    className="w-24 h-24 rounded-full object-cover"
                                />
                            ) : (
                                <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
                                    <User className="w-12 h-12 text-primary" />
                                </div>
                            )}

                            <div className="flex-1">
                                <h2 className="text-2xl font-heading font-bold mb-1">{faculty.name}</h2>
                                <p className="text-muted-foreground mb-4">{faculty.qualification}</p>

                                <div className="grid md:grid-cols-2 gap-4 mt-6">
                                    <div>
                                        <label className="text-sm font-medium text-muted-foreground">Email</label>
                                        <p className="font-medium">{faculty.email}</p>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-muted-foreground">Phone</label>
                                        <p className="font-medium">{faculty.phone}</p>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-muted-foreground">Experience</label>
                                        <p className="font-medium">{faculty.experience_years} years</p>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-muted-foreground">Rating</label>
                                        <p className="font-medium">
                                            {faculty.rating > 0 ? `${faculty.rating.toFixed(1)} ⭐` : 'Not rated yet'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {faculty.subject_specialization && faculty.subject_specialization.length > 0 && (
                            <div className="mt-6 pt-6 border-t">
                                <h3 className="font-semibold mb-2">Subject Specialization</h3>
                                <div className="flex flex-wrap gap-2">
                                    {faculty.subject_specialization.map((subject, index) => (
                                        <span
                                            key={index}
                                            className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm"
                                        >
                                            {subject}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {faculty.bio && (
                            <div className="mt-6 pt-6 border-t">
                                <h3 className="font-semibold mb-2">Bio</h3>
                                <p className="text-muted-foreground whitespace-pre-wrap">{faculty.bio}</p>
                            </div>
                        )}

                        {faculty.achievements && faculty.achievements.length > 0 && (
                            <div className="mt-6 pt-6 border-t">
                                <h3 className="font-semibold mb-2">Achievements</h3>
                                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                                    {faculty.achievements.map((achievement, index) => (
                                        <li key={index}>{achievement}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </FacultyLayout>
    );
};

export default FacultyProfile;
